module Meevo.Plugins.MembershipManager {
    "use strict";
    import IMembershipFreezeSetting = Meevo.Model.IMembershipFreezeSetting;
    import ICancellationDates = Meevo.Components.Common.ICancellationDates;
    import ClientMembershipIndicatorStatusEnum = Meevo.Model.ClientMembershipIndicatorStatusEnum;
    import ClientMembershipStatusEnum = Meevo.Model.ClientMembershipStatusEnum;
    import AutoRenewTypeEnum = Meevo.Model.AutoRenewTypeEnum;
    import BillingTypeEnum = Meevo.Model.BillingTypeEnum;
    import ClientMembershipBillStatusEnum = Meevo.Model.ClientMembershipBillStatusEnum;
    import ClientMembershipBillFeeTypeEnum = Meevo.Model.ClientMembershipBillFeeTypeEnum;

    /**
     * Main Section of Client
     */
    export class MainSection extends MembershipBaseSection {

        private context: MembershipManagerContext;
        private membershipInfo: Meevo.Model.Membership;
        public membershipList: Array<Meevo.Model.Membership>;
        public freezeLengthMin: any;
        public freezeLengthMax: any;
        public minDateError: string = '';
        public maxDateError: string = '';
        public reasonForFreezePlaceholder: string;
        public tempAccessDuringFreeze: boolean = false;
        public membershipAccessDuringFreezeSetting: Meevo.Model.IMembershipFreezeSetting;
        public cancellationDatesSettings: ICancellationDates;
        public membershipInFreezeStatus: boolean;
        public today: Date;
        public previewTerminationDateTill: Date;
        private pendingFreezeStatus: boolean;
        public securityKeys = Meevo.Components.Common.SecurityKeys;
        public backupModel: Meevo.Model.ClientMembership;
        public autoRenewTypeModel: AutoRenewTypeEnum;
        public isRenewalBillWasAttempted: boolean;
        public initialScheduledReactivationDate: Date;
        public showAccruedServicesExpiredDate: boolean;
        public enrollmentMinDate: Date = undefined;
        public enrollmentMaxDate: Date = undefined;

        constructor(params) {
            super(params);
            this.context = params.context;
        }

        /**
         * Check if membership payment day of month was changed in this month to the day before today and the membership
         * still has a bill to pay in this month
         * Related Item: #49590
         *
         * @example
         * EFT is scheduled to be billed for the 29th and on 4/22 the user changed the EFT day of the month to the 5th.
         * Because the 5th had already passed, the 4/29 bill date was unaffected, only May thru later bills were changed
         * to the 5th. If the user tries to freeze the membership on 4/23, they should get a yellow pop up that says:
         * "EFT to be processed on X date won't be frozen due to the EFT payment day of month being adjusted.
         * Are you sure you want to continue?"
         */
        public checkNextEFTWontBeFrozen(): ng.IPromise<any> {
            const { $q, modalService, $translate, $filter } = this.params;
            const { freezeStartDate, scheduledReactivationDate, isFrozen } = this.model;
            const today = new Date();
            const paymentDayOfMonth = this.model.paymentDayOfMonth;
            const meevoDateFilter = $filter('meevoDateTimeFilter') as any;

            if (!isFrozen) return $q.resolve();

            // get the next not paid bill
            const nextBill = _.sortBy(this.model.originMembershipBills, 'paymentDate')
                .filter(bill =>
                        bill.isScheduled // is recurring bill
                        && !bill.saleDate // not paid bill
                        && new Date(bill.paymentDate) >= today // bill in the future
                        && [
                            ClientMembershipBillFeeTypeEnum.RecurringFee,
                            ClientMembershipBillFeeTypeEnum.RenewalFee,
                        ].indexOf(bill.clientMembershipBillFeeType) !== -1
                )[0];

            if (!nextBill) return $q.resolve();

            const currentMonth = today.getMonth();
            const billPaymentDate = new Date(nextBill.paymentDate);
            const billMonth = billPaymentDate.getMonth();
            const billDay = billPaymentDate.getDate();
            const billWillBePaid = billMonth === currentMonth && paymentDayOfMonth !== billDay;
            const billInFreezeRange = new Date(freezeStartDate) < billPaymentDate && billPaymentDate < new Date(scheduledReactivationDate);

            if (billWillBePaid && billInFreezeRange) {
                return modalService.yellowConfirm(
                    $translate.instant('MESSAGE_NEXT_EFT_WONT_BE_FROZEN', {
                        date: meevoDateFilter(nextBill.paymentDate, false)
                    })
                );
            }

            return $q.resolve();
        }

        public sectionModelRetrieved(): void {
            this.model.renewalFee = this.model.membershipMemberSettings[0].renewalFee;
            super.sectionModelRetrieved();
            this.takeBackup();
            this.pendingToSaveFreezeStatus = false;
            this.today = new Date();
            this.today.setHours(0,0,0,0);
            this.tempAccessDuringFreeze = angular.isDefined(this.model.freezeSettingEnum);
            this.reasonForFreezePlaceholder = this.params.$translate.instant('LABEL_MEMBERSHIP_MANAGER_REASON_FOR_FREEZE');
            this.membershipInFreezeStatus = this.model.freezeStartDate ? true : false

            this.backupModel = angular.copy(this.model);
            this.autoRenewTypeModel = this.model.autoRenewEnabled ? this.model.autoRenewTypeEnum : AutoRenewTypeEnum.No;

            // Go back from CC/ACH tab
            if (this.params['context'].autoRenewSettings && this.params['context'].autoRenewSettings.autoRenewEnabled == true) {

                this.params['context'].setMode(Meevo.Model.MaintenanceFormState.Readonly);
                this.params['context'].edit();

                this.model.autoRenewEnabled = true;
                this.model.autoRenewTypeEnum = this.params['context'].autoRenewSettings.autoRenewTypeEnum;
                this.params['context'].autoRenewSettings = null;
                this.params['context'].save();
            }

            // calculate renewal paid bills
            let renewalAttemptedPaymentsList = _.chain(this.model.originMembershipBills).filter((bill: any) => {
                return !bill.isManualBill && bill.clientMembershipBillFeeType === ClientMembershipBillFeeTypeEnum.RenewalFee &&
                        (bill.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.Deleted &&
                         bill.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.NotBilled &&
                         bill.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.RemovedDONOTSHOW)
                }).value();

            this.isRenewalBillWasAttempted = renewalAttemptedPaymentsList.length > 0;

            if (this.isEditEnrollmentDate) {
                if (!this.model.saleDate || this.model.saleDate >= this.today) {
                    this.enrollmentMinDate = this.today;
                    this.enrollmentMaxDate = undefined;
                } else {
                    this.enrollmentMinDate = this.compareDateWithToday(this.model.enrollmentDate) ? moment(this.today).add(2, 'days').toDate() : undefined;
                    this.enrollmentMaxDate = this.compareDateWithToday(this.model.enrollmentDate) ? moment(this.model.enrollmentDate).add(6, 'months').toDate() : undefined;
                }
            }
        }

        /**
         * Load additional data for every section. Override method in membershipManager.base.section
         * */
        public loadDependentData(): ng.IPromise<any> {
            this.initialScheduledReactivationDate = this.model.scheduledReactivationDate;

            if (this.isInCancellation) {
                return this.params.$q.all([
                    this.loadMembershipInfo(),
                    this.loadMembershipList()
                ]);
            } else {
                return this.params.$q.all([
                    this.loadMembershipInfo(),
                    this.loadMembershipList()
                ]);
            }
        }

        /**
         * Parse data before sending to server. Override base class method
         * */
        public preSave(): void {
            if (this.model && this.membershipAccessDuringFreezeSetting) {
                this.model.freezeSettingEnum = this.membershipAccessDuringFreezeSetting.freezeSetting;
            }
        }

        private loadMembershipList(): ng.IPromise<any> {
            return this.dataService.getListPaged('membership', {
                criteria: {
                    maintenanceMode: true,
                    coSearchResultFilter: Meevo.Model.CoSearchResultsFilter.All,
                    coAvailableResultsFilter: Meevo.Model.CoAvailableResultsFilter.All,
                    coSearchType: this.identityService.inCentralOfficeMode() ? Meevo.Model.CoSearchType.Location : null,
                    coFilterByLocationId: this.model.homeLocationId,
                    includeActive: true,
                    includeInactive: this.params.securityChecker.hasSecurity(this.securityKeys.MEMBERSHIP_MEMBERSHIPMANAGER_ACTION_SWITCHTOINACTIVE),
                    location: {
                        id: this.model.homeLocationId,
                        locationId: this.model.homeLocationId,
                    }
                },
                itemsPerPage: 100000,
                pageNumber: 0
            })
                .then((data: Array<Meevo.Model.Membership>|any) => {
                    this.membershipList = data;
                })
        }

        /**
         * Get info of current membership
         * */
        private loadMembershipInfo(): ng.IPromise<any> {

            return this.dataService.getItem('membership', this.model.membershipId)
                .then((data:Meevo.Model.Membership|any) => {
                    this.membershipInfo = <Meevo.Model.Membership>data;
                    this.updateFreezeDates(this.model.freezeStartDate);
                    this.parseFreezeSettings();
                    this.minDateError = `The minimum freeze period is ${this.membershipInfo.minFreezeDays} days.  The date will automatically be adjusted to the minimum scheduled reactivation date.`;
                    this.maxDateError = `The maximum freeze period is ${this.membershipInfo.maxFreezeDays} days.  The date will automatically be adjusted to the maximum scheduled reactivation date.`;
                    if (this.model.freezeSettingEnum) {
                        this.membershipAccessDuringFreezeSetting = _.findWhere(this.membershipInfo.membershipFreezeSettings, {'freezeSetting': this.model.freezeSettingEnum});
                    } else if (this.membershipInfo.membershipFreezeSettings.length >= 1) {
                        this.membershipAccessDuringFreezeSetting = this.membershipInfo.membershipFreezeSettings[0];
                    }

                    this.showAccruedServicesExpiredDate = !this.membershipInfo.isAccruedServiceDaysToExpireEnabled || (this.membershipInfo.isAccruedServiceDaysToExpireEnabled && this.model.accruedServiceExpireDate) ? true : false;
                })
        }

        private loadCancellationDatesSettings(requestData?, showLoading: boolean = false): ng.IPromise<any> {
            let guidId = new Guid(this.model.id);

            if (this.model.clientMembershipIndicatorStatus === ClientMembershipIndicatorStatusEnum.InCancellation ||
                this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.Cancelled ||
                this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.CancelBeforeEnrollment ||
                this.model.cancelledDate) {
                return this.params.dataService.getDatesForCancellation(guidId, requestData, showLoading)
                    .then((data: ICancellationDates | any) => {
                        this.cancellationDatesSettings = data;
                    });
            } else {
                let deferred = this.params.$q.defer();

                deferred.resolve();
                return deferred.promise;
            }
        }

        get previewCancellationDateFrom() {
            return this.cancellationDatesSettings && this.cancellationDatesSettings.cancellationDateFrom && !this.params['context'].isReadonly ? this.cancellationDatesSettings.cancellationDateFrom : null;
        }
        get previewTerminationDateFrom() {
            return this.cancellationDatesSettings && this.cancellationDatesSettings.terminationDateFrom && !this.params['context'].isReadonly ? this.cancellationDatesSettings.terminationDateFrom : null;
        }

        public changeCancelledDate(): void {
            this.loadCancellationDatesSettings({ cancellationDate: this.model.cancelledDate }, true)
                .then((data) => {
                    this.previewTerminationDateTill = this.cancellationDatesSettings.maxTerminationDate;
                    // $timeout for update min/max date in datePicker
                    this.params.$timeout(() => {
                        this.model.terminationDate = this.cancellationDatesSettings.terminationDateTill;
                        this.model.accruedServiceExpireDate = this.cancellationDatesSettings.accruedServiceExpireDate;
                    });
                    this.model.cancellationState = Meevo.Model.ClientMembershipCancellationStateEnum.Cancellation;
                });
        }

        public changeTerminationDate(): void {
            let requestData = {
                cancellationDate: this.model.cancelledDate,
                terminationDate: this.model.terminationDate
            };

            this.loadCancellationDatesSettings(requestData, true)
                .then((data) => {
                    // $timeout for update min/max date in datePicker
                    this.params.$timeout(() => {
                        this.model.accruedServiceExpireDate = this.cancellationDatesSettings.accruedServiceExpireDate;
                    });
                    this.model.cancellationState = Meevo.Model.ClientMembershipCancellationStateEnum.Cancellation;
                });
        }

        public focusTerminationDate(): void {
            this.previewTerminationDateTill = this.cancellationDatesSettings.maxTerminationDate;
        }

        public postCancel() {
            this.previewTerminationDateTill = this.cancellationDatesSettings.maxTerminationDate;
            // $timeout for update min/max date in datePicker
            this.params.$timeout(() => {
                this.model.terminationDate = angular.copy(this.model.terminationDate);
            });
        }

        public validateCanEdit() {
            return this.model.clientMembershipStatusEnum !== Model.ClientMembershipStatusEnum.CancelBeforeEnrollment &&
                this.model.clientMembershipStatusEnum !== Model.ClientMembershipStatusEnum.Cancelled
        }

        public get isActiveStatus(): boolean {
            if (this.model && (this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.Cancelled ||
                this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.CancelBeforeEnrollment ||
                this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.Expired ||
                this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.InActive)) {
                return false;
            } else {
                return true;
            }
        }

        public get isNotActiveStatus(): boolean {
            if (this.model) {
                return this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.InActive;
            } else {
                return false;
            }
        }

        public get isFrozenStatus(): boolean {
            if (this.model) {
                return this.model.hasOwnProperty('freezeStartDate') && this.model.hasOwnProperty('scheduledReactivationDate');
            } else {
                return false;
            }
        }

        public get isRecurringPayments(): boolean {
            if (this.membershipInfo) {
                return this.membershipInfo.billingTypeEnum === BillingTypeEnum.RecurringPayments;
            } else {
                return false;
            }
        }

        public get pendingToSaveFreezeStatus(): boolean {
            return this.pendingFreezeStatus
        }

        public set pendingToSaveFreezeStatus(status: boolean) {
            this.pendingFreezeStatus = status;
        }

        public get membershipDisplayName(): string {
            if (this.membershipInfo) {
                return this.membershipInfo.displayName;
            } else {
                return '';
            }
        }

        public get isInCancellation(): boolean {
            return this.model && this.model.clientMembershipIndicatorStatus === ClientMembershipIndicatorStatusEnum.InCancellation;
        }

        public get isTerminated(): boolean {
            return this.model && (this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.Cancelled
                || this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.CancelBeforeEnrollment);
        }

        public get daysToTermination(): number {
            if (this.model && this.isInCancellation) {
                return Math.abs(moment(this.model.terminationDate).diff(moment().startOf('day'), 'days'));
            } else {
                return 0;
            }
        }

        public get isEditEnrollmentDate(): boolean {
            return this.model
                && (this.compareDateWithToday(this.model.enrollmentDate) || (!this.model.saleDate || this.model.saleDate >= this.today))
                && this.model.clientMembershipIndicatorStatus !== ClientMembershipIndicatorStatusEnum.Frozen
                && !this.model.freezeStartDate
                && !this.model.cancelledDate
        }

        public editEnrollmentDate() {
            if (this.context.isEditing) {

                if (this.model.cancelledDate) {
                    this.params.modalService.alert("MESSAGE_UNDO_CANCELLATION_TO_CHANGE_ENROLLMENT_DATE");

                } else if (this.model.freezeStartDate) {
                    this.params.modalService.alert("MESSAGE_UNFREEZE_MEMBERSHIP_TO_CHANGE_ENROLLMENT_DATE");
                }
            }
        }

        /**
         * Check if membership has recurring bill type
         * */
        public isRecurring(item: Meevo.Model.Membership): boolean {
            return item && item.billingTypeEnum === BillingTypeEnum.RecurringPayments;
        }

        /**
         * Compare input date with today date.
         * Returns true if input is before 2 days before the membership is expected to start (WI-26956)
         * */
        public compareDateWithToday(input: string|Date): boolean {
            return moment(input).subtract(2, 'days').toDate() >= this.today;
        }

        /**
         * Execute after enrollment date was changed
         * Update all other dependent dates
         * */
        public updateMembershipDates(): void {
            this.params.dataService.getDateDependencies(this.model.membershipId, this.model.enrollmentDate, true, null, this.model.paymentDayOfMonth, null, this.model.autoRenewEnabled)
                .then((data: Meevo.Components.Common.IClientMembershipDates) => {
                    data.expiresOnDate && (this.model.expiresOnDate = moment(data.expiresOnDate).toDate());
                    data.renewalBeginsOn && (this.model.renewalBeginsOn = moment(data.renewalBeginsOn).toDate());
                });
        }

        validateFreezeUnfreeze(): ng.IPromise<any> {
            return this.dataService.customMethodData(`/api/membership/client/${this.model.id}`, "GET", {}).then(resp => {
                var membership = resp.data;

                let hasPaidRenewalBill = _(membership.originMembershipBills).any(bill =>
                    bill.clientMembershipBillFeeType == Meevo.Model.ClientMembershipBillFeeTypeEnum.RenewalFee &&
                    bill.clientMembershipBillStatus == Meevo.Model.ClientMembershipBillStatusEnum.Paid);

                if (hasPaidRenewalBill &&
                    membership.billingTypeEnum == BillingTypeEnum.PaidInFull &&
                    membership.isInInitialTerm) {
                    this.params.modalService.alert("MESSAGE_MEMBERSHIP_FREEZE_UNFREEZE");
                    return false;
                }
                return true;
            })
        }

        /**
         * Freeze client membership
         * */
        public freeze(): void {
            this.validateFreezeUnfreeze().then(isValid => { 
                if (!isValid) {
                    return;
                }

                if (this.model.cancelledDate) {
                    this.params.modalService.alert("MESSAGE_UNDO_CANCELLATION_TO_FREEZE_MEMBERSHIP");
                } else {

                    if (this.isActiveStatus) {
                        this.pendingToSaveFreezeStatus = true;
                        this.model.isFrozen = true;
                        this.model.freezeStartDate = moment().startOf('day').toDate();
                        //this.model.scheduledReactivationDate = this.freezeLengthMax.toDate();
                        this.updateFreezeDates(this.model.freezeStartDate, true);
                    } else {
                        this.params.modalService.alert('MESSAGE_MEMBERSHIPMANAGER_FREEZE_NOT_ACTIVE_MEMBERSHIP', Meevo.Components.Modal.ModalTranslationModes.Translate)
                    }
                }
            });
        }

        /**
         * Unfreeze client membership
         * */
        public unfreeze(): void {
            this.validateFreezeUnfreeze().then(isValid => {
                if (!isValid) {
                    return;
                }
                if (this.model.cancelledDate) {
                    this.params.modalService.alert("MESSAGE_UNDO_CANCELLATION_TO_UNFREEZE_MEMBERSHIP");
                } else {
                    if (this.validateMinimumFreezePeriod()) {
                        this.params.modalService.confirmHandler('MESSAGE_MEMBERSHIPMANAGER_UNFREEZE_MINIMUM_PERIOD', Meevo.Components.Modal.ModalTranslationModes.Translate)
                            .then((response) => {
                                if (response) {
                                    this.pendingToSaveFreezeStatus = true;
                                    this.model.isFrozen = false;
                                    delete this.model.freezeStartDate;
                                }
                            })
                    } else {
                        this.pendingToSaveFreezeStatus = true;
                        this.model.isFrozen = false;
                        delete this.model.freezeStartDate;
                    }
                }
            });
        }

        /**
         * Check if minimum freeze period was met
         * */
        private validateMinimumFreezePeriod(): boolean {
            let minFreezePeriod = moment(this.model.freezeStartDate).startOf('day').add(this.membershipInfo.minFreezeDays);

            return minFreezePeriod.isAfter(moment().startOf('day'));
        }

        /**
         * Trigger if  reactivation date was updated by user
         * */
        public wasManuallyUpdated(): void {
            if (this.model.scheduledReactivationDate != this.freezeLengthMax.toDate()) {
                this.model.scheduledReactivationManuallySet = true;
                this.model.isFrozen = true;
                if (!this.pendingToSaveFreezeStatus) {
                    this.model.scheduledReactivationChanged = true;
                }
            }
        }

        /**
         * Calculate Reactivation date according to input date
         * */
        public updateFreezeDates(inputDate: string|Date = new Date(), setReactivationDate: boolean = false): void {
            let mDateForMin = moment(inputDate).startOf('day'),     // we need two variables because
                mDateForMax = moment(inputDate).startOf('day'),     // moment().add() function is mutable.
                scheduledDateMax = moment(inputDate).startOf('day'),
                freezeDateIsValid = this.validateFreezeDates();

            if (!freezeDateIsValid) {
                this.model.freezeStartDate = moment().startOf('day').toDate();
                mDateForMax = moment(this.model.freezeStartDate).startOf('day');
                scheduledDateMax = moment(this.model.freezeStartDate).startOf('day');

                this.params.securityChecker.checkSecurityKey(this.securityKeys.MEMBERSHIP_MEMBERSHIPMANAGER_ACTION_OVERRIDEREACTIVATIONDATE)
                    .then((securityCode: Meevo.Model.SecurityAccessEnum) => {
                        let maxFreezeDays = securityCode === Meevo.Model.SecurityAccessEnum.Grant ? this.membershipInfo.maxFreezeDays + 365 : this.membershipInfo.maxFreezeDays,
                            freezeLengthMaxDate = scheduledDateMax.add(this.membershipInfo.maxFreezeDays, 'd').startOf('day');
                        this.freezeLengthMax = mDateForMax.add(maxFreezeDays, 'd').startOf('day');
                        this.model.scheduledReactivationDate = freezeLengthMaxDate.toDate();
                    });
            } else {
                this.freezeLengthMin = mDateForMin.add(this.membershipInfo.minFreezeDays, 'd').startOf('day');
                this.params.securityChecker.checkSecurityKey(this.securityKeys.MEMBERSHIP_MEMBERSHIPMANAGER_ACTION_OVERRIDEREACTIVATIONDATE)
                    .then((securityCode: Meevo.Model.SecurityAccessEnum) => {
                        let maxFreezeDays = securityCode === Meevo.Model.SecurityAccessEnum.Grant ? this.membershipInfo.maxFreezeDays + 365 : this.membershipInfo.maxFreezeDays;

                        // calculate based on original mDateForMax value. and preserve original value via clone().
                        let freezeLengthMaxReactivationDate = mDateForMax.clone().add(maxFreezeDays, 'd').startOf('day').toDate();

                        if (this.initialScheduledReactivationDate > freezeLengthMaxReactivationDate) {
                            this.freezeLengthMax = moment(this.initialScheduledReactivationDate).startOf('day')
                            this.model.scheduledReactivationDate = this.initialScheduledReactivationDate;
                        } else {
                            this.freezeLengthMax = mDateForMax.add(maxFreezeDays, 'd').startOf('day'); // calculate based on original mDateForMax value
                        }
                    });

                if (setReactivationDate) {
                    // wait until freezeLengthMin and freezeLengthMax are updated as min and max values in datePicker directive
                    this.params.$timeout(() => {
                        let freezeLengthMaxDate = scheduledDateMax.add(this.membershipInfo.maxFreezeDays, 'd').startOf('day');

                        this.model.scheduledReactivationDate = freezeLengthMaxDate.toDate();
                    }, 0);
                }
            }
        }

        /**
         * Validate freeze date
         * */
        public validateFreezeDates(): boolean {
            let billingType = this.model.billingTypeEnum,
                autoRenewType = this.model.autoRenewTypeEnum,
                freezeStartDate = this.model.freezeStartDate,
                oneYearFromCurrentDate = moment(this.today).add(1, 'year').toDate(),
                initialDateFromModel = moment(this.model.initialTerm).add(1, 'days').toDate(),
                isMembershipInInitialTerm = this.today < initialDateFromModel;

            if (freezeStartDate >= this.model.renewalBeginsOn &&
                (autoRenewType === AutoRenewTypeEnum.No || !this.model.autoRenewEnabled)) {
                this.params.modalService.alert("MESSAGE_FREEZE_NO_AUTO_RENEW");
                return false;
            }

            if (billingType === BillingTypeEnum.PaidInFull &&
                autoRenewType === AutoRenewTypeEnum.PaidInFull &&
                freezeStartDate >= this.model.renewalBeginsOn) {
                this.params.modalService.alert("MESSAGE_FREEZE_PIF_PIF_RENEWAL");
                return false;
            }

            if (isMembershipInInitialTerm) {
                if (autoRenewType === AutoRenewTypeEnum.Monthly && freezeStartDate >= initialDateFromModel) {
                    this.params.modalService.alert("MESSAGE_FREEZE_PIF_RENEWAL");
                    return false;
                } else {
                    return true;
                }
            } else {
                if (freezeStartDate > oneYearFromCurrentDate) {
                    this.params.modalService.alert("MESSAGE_FREEZE_MONTHLY_RENEWAL");
                    return false;
                } else {
                    return true;
                }
            }
        }


        /**
         * Check if auto renew checkbox is available
         * */
        public get validateAutoRenew(): boolean {
            return this.model
                && (this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.Cancelled
                    || this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.CancelBeforeEnrollment
                    || this.model.clientMembershipStatusEnum === ClientMembershipStatusEnum.Expired
                    || this.model.clientMembershipIndicatorStatus === ClientMembershipIndicatorStatusEnum.Frozen
                    || (this.membershipInfo.autoRenewTypeEnum === AutoRenewTypeEnum.No && this.model.autoRenewEnabled == false))
        }

        /**
         * Validate auto renew checkbox on change and move to CC/ACH tab
         * */
        public changeAutoRenew() {
            if (this.model.autoRenewEnabled === true && this.model.paymentAccounts.length < 1) {
                this.params.modalService.alert("MESSAGE_MEMBERSHIPMANAGER_AUTORENEW_NEED_PAYMENT").then(() => {
                    if (!this.params['context'].autoRenewSettings) {
                        this.params['context'].autoRenewSettings = {};
                    }
                    this.params['context'].autoRenewSettings.autoRenewEnabled = true;
                    this.params['context'].autoRenewSettings.autoRenewTypeEnum = this.model.autoRenewTypeEnum;

                    // GO to CC/ACH tab
                    this.params['context'].selectSection(this.params['context'].sections[1]);
                });
            }
        }


        public changeAutoRenewTypeEnum (): void {
            let autoRenewTypeEnum = this.autoRenewTypeModel,
                autoRenewRenewalFee = this.model.membershipMemberSettings[0],
                bothAutoRenewMembership = this.membershipInfo ? this.membershipInfo.autoRenewTypeEnum === AutoRenewTypeEnum.Both : false;

            if (bothAutoRenewMembership && this.backupModel.autoRenewTypeEnum !== autoRenewTypeEnum) {
                if (autoRenewTypeEnum !== AutoRenewTypeEnum.No) {
                    let futureAttemptedPaymentsList = _.chain(this.model.originMembershipBills).filter((bill: any) => {
                        return !bill.isManualBill && bill.paymentDate > this.today &&
                                   (bill.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.Deleted &&
                                    bill.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.NotBilled &&
                                    bill.clientMembershipBillStatus !== ClientMembershipBillStatusEnum.RemovedDONOTSHOW)
                        }).value();

                    if (this.model.freezeStartDate) {
                        this.params.modalService.alert("MESSAGE_CHANGE_RENEWAL_TYPE_FREEZE");
                        this.autoRenewTypeModel = this.model.autoRenewEnabled ? this.backupModel.autoRenewTypeEnum : AutoRenewTypeEnum.No;
                        return;
                    } else if (futureAttemptedPaymentsList.length > 0) {
                        this.params.modalService.alert("MESSAGE_CHANGE_RENEWAL_TYPE_FUTURE_BILLS");
                        this.autoRenewTypeModel = this.model.autoRenewEnabled ? this.backupModel.autoRenewTypeEnum : AutoRenewTypeEnum.No;
                        return;
                    }
                } else {
                    this.model.autoRenewEnabled = false;
                    this.model.autoRenewTypeEnum = this.backupModel.autoRenewTypeEnum;
                    this.autoRenewTypeModel = AutoRenewTypeEnum.No;
                    return;
                }
            }

            this.model.autoRenewEnabled = !(autoRenewTypeEnum === AutoRenewTypeEnum.No);
            this.model.autoRenewTypeEnum = this.autoRenewTypeModel;

            if ((autoRenewTypeEnum === AutoRenewTypeEnum.PaidInFull && autoRenewRenewalFee.pifRenewalFee > 0) ||
                (autoRenewTypeEnum === AutoRenewTypeEnum.Monthly && autoRenewRenewalFee.monthlyRenewalFee > 0)) {
                this.changeAutoRenew();
            }
        }

        public isDisabledAutoRenewTypeEnum () {
            return false;
        }

        public isShowOptionAutoRenewTypeEnum(autoRenewTypeEnum?: AutoRenewTypeEnum) {
            return this.membershipInfo &&
                (
                (this.membershipInfo.autoRenewTypeEnum === AutoRenewTypeEnum.Both && !(this.isRenewalBillWasAttempted && this.model.autoRenewTypeEnum !== autoRenewTypeEnum)) ||
                this.model.autoRenewTypeEnum === autoRenewTypeEnum ||
                this.membershipInfo.autoRenewTypeEnum === autoRenewTypeEnum
                );
        }

        /**
         * Parse freeze fees settings which come from membership model
         * */
        private parseFreezeSettings(): void {
            this.membershipInfo.membershipFreezeSettings.forEach((item:IMembershipFreezeSetting) => {
                item.displayName = this.translateForFreezeSetting(item);
            });

            if (this.membershipInfo.membershipFreezeSettings.length === 0 && this.isFrozenStatus && this.model.freezeSettingEnum) {
                let item: IMembershipFreezeSetting = {
                    freezeSetting: this.model.freezeSettingEnum,
                    fee: null,
                    isPercent: undefined
                }

                item.displayName = this.translateForFreezeSetting(item);

                this.membershipInfo.membershipFreezeSettings.push(item);
            }
        }

        private translateForFreezeSetting(item: IMembershipFreezeSetting): string {
            switch (item.freezeSetting) {
                case Meevo.Model.FreezeSettingEnum.NoAccess:
                    return this.params.$translate.instant('LABEL_NO_ACCESS');
                case Meevo.Model.FreezeSettingEnum.AccruedServiceOnly:
                    return this.params.$translate.instant('LABEL_ACCRUED_SERVICES_ONLY');
                case Meevo.Model.FreezeSettingEnum.FullAccess:
                    return this.params.$translate.instant('LABEL_FULL_ACCESS?');
                case Meevo.Model.FreezeSettingEnum.MemberBenefitsOnly:
                    return this.params.$translate.instant('LABEL_MEMBER_BENEFITS_ONLY');
            }
        }
    }
}
